import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PaymentGateway from '../Components/PaymentGateway';
import CheckoutSteps from '../Components/CheckoutSteps';
import { ORDER_CREATE_RESET } from '../Constants/orderConstants';
import '../Components/PaymentGateway.css';
import { savePaymentMethod } from '../Actions/cartActions';
import { useToast } from '../Context/ToastContext';
import { firestore } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const PaymentScreen = ({ match }) => {
  const routeOrderId = match.params.orderId;
  const history = useHistory();
  const dispatch = useDispatch();
  const { success: toastSuccess } = useToast();
  
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [message, setMessage] = useState('');
  const unblockRef = useRef(null);

  const orderCreate = useSelector((state) => state.orderCreate);
  const { success: orderSuccess, orderId: createdOrderId, paymentMethod, totalAmount } = orderCreate || {};
  const activeOrderId = createdOrderId || routeOrderId;
  
  // Debug: Log payment screen state
  console.log('PaymentScreen Debug:', {
    routeOrderId,
    createdOrderId,
    activeOrderId,
    paymentMethod,
    totalAmount,
    orderSuccess
  });

  useEffect(() => {
    // If we have an order id in route or state, stay on payment screen; otherwise go back to cart
    if (!activeOrderId) {
      history.push('/cart');
      return;
    }
  }, [activeOrderId, history]);

  // Intercept browser back to ensure cancellation and redirect to cart
  useEffect(() => {
    // Block any navigation away while payment is pending/failed
    const unblock = history.block((tx) => {
      if (paymentStatus !== 'success') {
        // cancel and go to cart
        dispatch({ type: ORDER_CREATE_RESET });
        try { dispatch(savePaymentMethod('')); } catch (_) {}
        try { localStorage.removeItem('paymentMethod'); } catch (_) {}
        // allow this one redirect to complete
        if (unblockRef.current) {
          const fn = unblockRef.current; unblockRef.current = null; try { fn(); } catch (_) {}
        }
        history.replace('/cart');
        return false; // prevent default navigation
      }
      return undefined;
    });
    unblockRef.current = unblock;
    return () => {
      if (unblockRef.current) { const fn = unblockRef.current; unblockRef.current = null; try { fn(); } catch (_) {} }
    };
  }, [history, dispatch, paymentStatus]);

  // Fallback: listen for order doc turning Paid and redirect automatically
  useEffect(() => {
    if (!activeOrderId || paymentStatus === 'success') return;
    const ref = doc(firestore, 'ORDERS', activeOrderId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const ps = String(data.Payment_Status || '').toLowerCase();
      if (ps === 'paid') {
        handlePaymentSuccess({
          orderId: activeOrderId,
          amount: data.Total_Amount,
          paymentId: data.CustTransaction_No,
          gatewayOrderId: data.CustUTR_No,
          paymentMethod: data.Payment_Method,
          paid: true,
        });
      }
    });
    return () => unsub();
  }, [activeOrderId, paymentStatus]);

  const handlePaymentSuccess = (payload) => {
    const details = typeof payload === 'string' ? { message: payload } : (payload || {});
    setPaymentStatus('success');
    setMessage(details.message || 'Payment successful');
    try { toastSuccess('Payment successful. Redirecting to confirmation…'); } catch (_) {}
    const finalOrderId = details.orderId || activeOrderId;
    // Reset order creation state and navigate with full details
    dispatch({ type: ORDER_CREATE_RESET });
    try { localStorage.removeItem('paymentMethod'); } catch (_) {}
    // Important: unblock before programmatic navigation to avoid race with state update
    if (unblockRef.current) { const fn = unblockRef.current; unblockRef.current = null; try { fn(); } catch (_) {} }
    history.replace({
      pathname: '/order-success',
      search: finalOrderId ? `?orderId=${encodeURIComponent(finalOrderId)}` : '',
      state: {
        orderId: finalOrderId,
        amount: details.amount,
        paymentId: details.paymentId,
        gatewayOrderId: details.gatewayOrderId,
        paymentMethod: details.paymentMethod || paymentMethod,
        paid: details.paid === true,
      }
    });
  };

  const handlePaymentFailure = (errorMessage) => {
    setPaymentStatus('failed');
    setMessage(errorMessage);
  };

  const handleRetry = () => {
    setPaymentStatus('pending');
    setMessage('');
  };

  const cancelAndGoToCart = () => {
    // Clear the pending order state and clear saved payment method to avoid auto-selection on return
    dispatch({ type: ORDER_CREATE_RESET });
    try { dispatch(savePaymentMethod('')); } catch (_) {}
    try { localStorage.removeItem('paymentMethod'); } catch (_) {}
    history.replace('/cart');
  };

  // Cleanup when leaving this screen without success
  useEffect(() => {
    return () => {
      if (paymentStatus !== 'success') {
        try { dispatch(savePaymentMethod('')); } catch (_) {}
      }
    };
  }, [dispatch, paymentStatus]);

  if (paymentStatus === 'success') {
    return (
      <div className="payment-screen">
        <CheckoutSteps step1 step2 step3 step4></CheckoutSteps>
        <div className="payment-success">
          <div className="success-icon">✓</div>
          <h2>Payment Successful!</h2>
          <p>{message}</p>
          <p>Order ID: {activeOrderId}</p>
          <p>Redirecting to order confirmation...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="payment-screen">
        <CheckoutSteps step1 step2 step3 step4></CheckoutSteps>
        <div className="payment-failed">
          <div className="error-icon">✗</div>
          <h2>Payment Failed</h2>
          <p>{message}</p>
          <div className="retry-options">
            <button onClick={handleRetry} className="btn btn-primary">
              Try Again
            </button>
            <button onClick={() => history.push('/cart')} className="btn btn-secondary">
              Back to Cart
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-screen">
      <CheckoutSteps step1 step2 step3 step4></CheckoutSteps>
      <div className="payment-container">
        <h1>Complete Your Payment</h1>
        
        {activeOrderId && (
          <PaymentGateway
            orderId={activeOrderId}
            paymentMethod={paymentMethod}
            totalAmount={totalAmount}
            onSuccess={handlePaymentSuccess}
            onFailure={handlePaymentFailure}
          />
        )}

        <div className="payment-cancel">
          <button onClick={cancelAndGoToCart} className="btn btn-secondary" style={{ marginTop: 16 }}>
            Cancel and go to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentScreen;
